import React, { useState, useEffect } from "react";
import {
  searchUserByEmail,
  sendArtistMemberInvite,
} from "../../../../services/api";

const AddMembersModal = ({ isOpen, onClose, artistId, onInviteSent, members = [], currentUserId = null }) => {
  const [email, setEmail] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [inviteMessage, setInviteMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [canInvite, setCanInvite] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('none'); // 'none', 'already_member', 'pending', 'self', 'valid'
  const [statusMessage, setStatusMessage] = useState(null);

  const handleClose = () => {
    setEmail("");
    setSearchResult(null);
    setInviteMessage("");
    setSearchError(null);
    setSuccessMessage(null);
    setIsSearching(false);
    setIsSending(false);
    setCanInvite(false);
    setInviteStatus('none');
    setStatusMessage(null);
    onClose();
  };

  // Check edge cases when searchResult or members change
  useEffect(() => {
    if (!searchResult) {
      setCanInvite(false);
      setInviteStatus('none');
      setStatusMessage(null);
      return;
    }

    // Check self-invite
    if (searchResult.user_id === currentUserId) {
      setCanInvite(false);
      setInviteStatus('self');
      setStatusMessage("You cannot invite yourself to the group.");
      return;
    }

    // Check if already a member
    const isMember = members.some(
      m => m.user_id === searchResult.user_id && m.status !== 'pending'
    );
    if (isMember) {
      setCanInvite(false);
      setInviteStatus('already_member');
      setStatusMessage("This user is already a member of this artist group.");
      return;
    }

    // Check if pending invite exists (by user_id or email)
    const hasPending = members.some(
      m => (m.user_id === searchResult.user_id || m.email === searchResult.email) 
        && m.status === 'pending'
    );
    if (hasPending) {
      setCanInvite(false);
      setInviteStatus('pending');
      setStatusMessage("An invite is already pending for this user.");
      return;
    }

    // All checks passed
    setCanInvite(true);
    setInviteStatus('valid');
    setStatusMessage(null);
  }, [searchResult, members, currentUserId]);

  // Early return after all hooks are declared
  if (!isOpen) {
    return null;
  }

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchError(null);
    setSuccessMessage(null);
    setSearchResult(null);
    setStatusMessage(null);
    setCanInvite(false);
    setInviteStatus('none');

    if (!email.trim()) {
      setSearchError("Please enter an email.");
      return;
    }

    try {
      setIsSearching(true);
      const response = await searchUserByEmail(email.trim());
      if (response.success) {
        setSearchResult(response.user);
      } else {
        setSearchError(response.error || "User not found");
      }
    } catch (error) {
      setSearchResult(null);
      setSearchError(error.message || "User not found");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSendInvite = async () => {
    if (!searchResult?.user_id) {
      setSearchError("Search for a user before sending an invite.");
      return;
    }

    if (!canInvite) {
      setSearchError("Cannot send invite. Please check the status message.");
      return;
    }

    try {
      setIsSending(true);
      setSearchError(null);
      setStatusMessage(null);
      const response = await sendArtistMemberInvite(
        artistId,
        email.trim(),
        inviteMessage.trim() || null
      );
      if (response.success) {
        setSuccessMessage("Invite sent successfully.");
        setInviteMessage("");
        setSearchResult(null);
        setCanInvite(false);
        setInviteStatus('none');
        if (onInviteSent) {
          onInviteSent();
        }
      } else {
        // Handle API error responses
        const errorMsg = response.error || "Failed to send invite";
        setSearchError(errorMsg);
        
        // Update status based on specific error messages
        if (errorMsg.includes("already a member")) {
          setInviteStatus('already_member');
          setStatusMessage("This user is already a member of this artist group.");
        } else if (errorMsg.includes("already pending")) {
          setInviteStatus('pending');
          setStatusMessage("An invite is already pending for this user.");
        } else if (errorMsg.includes("Cannot invite yourself") || errorMsg.includes("invite yourself")) {
          setInviteStatus('self');
          setStatusMessage("You cannot invite yourself to the group.");
        }
        setCanInvite(false);
      }
    } catch (error) {
      // The API service throws errors with error.message containing the API error
      const errorMessage = error.message || "Failed to send invite";
      
      setSearchError(errorMessage);
      setCanInvite(false);
      
      // Update status based on error message
      if (errorMessage.includes("already a member")) {
        setInviteStatus('already_member');
        setStatusMessage("This user is already a member of this artist group.");
      } else if (errorMessage.includes("already pending")) {
        setInviteStatus('pending');
        setStatusMessage("An invite is already pending for this user.");
      } else if (errorMessage.includes("Cannot invite yourself") || errorMessage.includes("invite yourself")) {
        setInviteStatus('self');
        setStatusMessage("You cannot invite yourself to the group.");
      } else if (errorMessage.includes("Only owners can invite")) {
        setStatusMessage("Only owners can invite members to artists.");
      } else if (errorMessage.includes("User not found")) {
        setStatusMessage("User not found. Please verify the email address.");
      } else {
        // For other errors, show the error message
        setStatusMessage(errorMessage);
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="artist-profile-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="artist-profile-modal groups-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="artist-profile-modal-header">
          <h2>Add Members</h2>
          <button
            type="button"
            className="artist-profile-modal-close"
            aria-label="Close"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="artist-profile-modal-body groups-modal-body">
          <form className="groups-modal-section" onSubmit={handleSearch}>
            <div className="groups-modal-field">
              <label>
                <span className="groups-modal-field-label">Member Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="groups-modal-input"
                />
              </label>
            </div>
            <div className="groups-modal-actions">
              <button
                type="submit"
                className="groups-modal-primary-button"
                disabled={isSearching}
              >
                {isSearching ? "Searching..." : "Search"}
              </button>
            </div>
          </form>

          {searchError && (
            <p className="groups-modal-error" role="alert">
              {searchError}
            </p>
          )}

          {searchResult && (
            <div className="groups-modal-section">
              <h3 className="groups-modal-section-title">User Found</h3>
              <div className="groups-modal-field-row">
                <div className="groups-modal-field">
                  <span className="groups-modal-field-label">Name:</span>
                  <span className="groups-modal-field-value">
                    {searchResult.name || "N/A"}
                  </span>
                </div>
                <div className="groups-modal-field">
                  <span className="groups-modal-field-label">Email:</span>
                  <span className="groups-modal-field-value">
                    {searchResult.email}
                  </span>
                </div>
              </div>
              
              {/* Status Message */}
              {statusMessage && (
                <p 
                  className={
                    inviteStatus === 'valid' 
                      ? "groups-modal-success" 
                      : inviteStatus === 'self' 
                      ? "groups-modal-error" 
                      : "groups-modal-error"
                  }
                  role="alert"
                  style={{
                    marginTop: "1rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "6px",
                    ...(inviteStatus === 'already_member' || inviteStatus === 'pending' 
                      ? { 
                          background: "rgba(255, 193, 7, 0.1)", 
                          border: "1px solid rgba(255, 193, 7, 0.3)",
                          color: "#ffc107"
                        }
                      : {})
                  }}
                >
                  {statusMessage}
                </p>
              )}
            </div>
          )}

          <div className="groups-modal-section">
            <div className="groups-modal-field">
              <label>
                <span className="groups-modal-field-label">Message (optional)</span>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Add a short message..."
                  className="groups-modal-textarea"
                  rows={3}
                />
              </label>
            </div>
            <div className="groups-modal-actions">
              <button
                type="button"
                className="groups-modal-primary-button"
                onClick={handleSendInvite}
                disabled={!searchResult || !canInvite || isSending}
              >
                {isSending ? "Sending..." : "Send Invite"}
              </button>
              <button
                type="button"
                className="groups-modal-secondary-button"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
            {successMessage && (
              <p className="groups-modal-success">{successMessage}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMembersModal;
